import type { OverviewShipment, RouteDetail, RouteSummary } from '../domain/routes';
import type { PackageInfo } from '../domain/types';
import { request, tokenStore } from './client';
import { mapPackage } from './mapPackage';
import { mapOverview, mapRouteDetail, mapRouteList } from './mapRoutes';
import { mockOverview, mockPackage, mockRouteDetail, mockRoutes } from './mock';
import type {
  AuthResponseDto,
  FindByOrderRequestDto,
  OverviewResponseDto,
  PackageCodeRequestDto,
  PackageInfoDto,
  RouteDetailResponseDto,
  RouteListResponseDto,
  SessionContextResponseDto,
  SessionDto,
  UserLanguageCode,
  UserPreferencesResponseDto,
  WeightUpdateResponseDto,
} from './types';

export const mockMode = {
  enabled: new URLSearchParams(location.search).get('mock') === 'true',
};

const MOCK_SESSION: SessionDto = {
  userId: 'mock',
  userName: 'Mock Skladník',
  warehouseCode: 'CZ',
  assignedVehicle: 'L12',
  assignedRouteNumber: '118',
  language: 'cs',
  // `?mock=true&role=MASTER` – proklikání přehledu tras bez backendu.
  role:
    new URLSearchParams(location.search).get('role') === 'MASTER' ? 'MASTER' : 'WAREHOUSE',
};

/** Výsledek skenu: data zásilky + informace, jestli se povedlo zapsat načtení. */
export interface ScanResult {
  pkg: PackageInfo;
  /** `false` → zápis „Načteno skladem“ se nepovedl, aplikace ho dožene frontou. */
  scanLogged: boolean;
}

export async function login(name: string, pin: string): Promise<SessionDto> {
  if (mockMode.enabled) {
    tokenStore.set('mock-token');
    return MOCK_SESSION;
  }
  const data = await request<AuthResponseDto>('/app/v2/auth', {
    body: { name, pin },
    auth: false,
  });
  tokenStore.set(data.token);
  return data.session;
}

/** Kontext směny při startu aplikace s už uloženým tokenem. */
export async function getSessionContext(): Promise<SessionDto> {
  if (mockMode.enabled) return MOCK_SESSION;
  const data = await request<SessionContextResponseDto>('/app/v2/scanner/session-context', {
    method: 'GET',
  });
  return data.session;
}

export async function setLanguage(language: UserLanguageCode): Promise<SessionDto> {
  if (mockMode.enabled) return { ...MOCK_SESSION, language };
  const data = await request<UserPreferencesResponseDto>('/app/v2/user/preferences', {
    method: 'PUT',
    body: { language },
  });
  return data.session;
}

/**
 * Sken zásilky.
 *
 * Jedno volání načte data i zapíše „Načteno skladem“ — na skladové WiFi je
 * každé volání navíc riziko. Idempotenci drží `clientScanId`, takže síťový
 * retry nenafoukne počítadlo načtení.
 */
export async function scanPackage(code: string, options: { countAsScan?: boolean } = {}): Promise<ScanResult> {
  if (mockMode.enabled) return { pkg: await mockPackage(code), scanLogged: true };

  const payload: PackageCodeRequestDto = {
    code,
    clientScanId: crypto.randomUUID(),
    countAsScan: options.countAsScan ?? true,
    scannedAt: new Date().toISOString(),
  };

  const dto = await request<PackageInfoDto>('/app/v2/scanner/package-info', { body: payload });
  return { pkg: mapPackage(dto), scanLogged: dto.scanLogged };
}

/** Manuální dohledání podle čísla objednávky. Sken se nezapisuje. */
export async function findByOrder(orderNumber: string): Promise<ScanResult> {
  if (mockMode.enabled) return { pkg: await mockPackage(orderNumber), scanLogged: false };

  const payload: FindByOrderRequestDto = { orderNumber };
  const dto = await request<PackageInfoDto>('/app/v2/scanner/find-by-order', { body: payload });
  return { pkg: mapPackage(dto), scanLogged: false };
}

/**
 * Seznam tras pro daný den — přehled mistra (role `MASTER`).
 *
 * Čtecí endpoint, do offline fronty nepatří: fronta je od zápisů. Když není
 * signál, ukáže obrazovka poslední načtený stav a řekne, kdy byl načtený.
 */
export async function listRoutes(date: string): Promise<RouteSummary[]> {
  if (mockMode.enabled) return mockRoutes();
  const dto = await request<RouteListResponseDto>(
    `/app/v2/scanner/routes?date=${encodeURIComponent(date)}`,
    { method: 'GET' },
  );
  return mapRouteList(dto);
}

/** Detail trasy — zásilky s rozpadem na balíky (naskenováno / naloženo). */
export async function getRouteDetail(routeNo: string, date: string): Promise<RouteDetail> {
  if (mockMode.enabled) return mockRouteDetail(routeNo);
  const dto = await request<RouteDetailResponseDto>(
    `/app/v2/scanner/routes/${encodeURIComponent(routeNo)}?date=${encodeURIComponent(date)}`,
    { method: 'GET' },
  );
  return mapRouteDetail(dto);
}

/**
 * Rozskenované zásilky napříč trasami — řádek pod skenerem.
 *
 * Čtecí endpoint, do fronty nepatří (stejně jako trasy). Volá se při každém
 * návratu na sken, takže je schválně bez vlastního stavu: aktuální číslo je
 * důležitější než ušetřený dotaz — zastaralý počet by poslal skladníka hledat
 * balík, který mezitím někdo dodělal.
 */
export async function listPartiallyScanned(date: string): Promise<OverviewShipment[]> {
  if (mockMode.enabled) return mockOverview();
  const dto = await request<OverviewResponseDto>(
    `/app/v2/scanner/overview?date=${encodeURIComponent(date)}`,
    { method: 'GET' },
  );
  return mapOverview(dto);
}

/** Oprava váhy + fotky jako důkaz. */
export async function updatePackageWeight(input: {
  code: string;
  newWeightKg: number;
  files: FileList | File[] | null;
}): Promise<void> {
  if (mockMode.enabled) return;

  const fd = new FormData();
  fd.append('code', input.code);
  fd.append('newWeightKg', String(input.newWeightKg));
  fd.append('clientActionId', crypto.randomUUID());
  if (input.files) {
    for (const file of Array.from(input.files)) fd.append('files', file);
  }
  await request<WeightUpdateResponseDto>('/app/v2/scanner/weight-update', { formData: fd });
}
